import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { apiService } from '../services/apiService';
import Monogram from './layout/Monogram';

interface ChatUser {
  id: string;
  name: string;
  profilePicture: string | null;
  email: string;
}

interface Conversation {
  user: ChatUser;
  connectionType: string;
  lastMessage: { content: string; createdAt: string; isFromMe: boolean } | null;
  unreadCount: number;
}

interface Message {
  id: string;
  content: string;
  createdAt: string;
  isFromMe: boolean;
  sender: { id: string; name: string; profilePicture: string | null };
}

const roleLabel = (type: string): string => {
  if (type.endsWith('-mentor')) return 'Mentor';
  if (type.startsWith('mentor-')) return 'Student';
  if (type.endsWith('-counselor')) return 'Counselor';
  if (type.startsWith('counselor-')) return 'Student';
  return '';
};

const formatTime = (dateStr: string) =>
  new Date(dateStr).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

const formatConvoTime = (dateStr: string) => {
  const date = new Date(dateStr);
  const today = new Date();
  if (date.toDateString() === today.toDateString()) return formatTime(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatDayDivider = (dateStr: string): string => {
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
};

const groupByDay = (msgs: Message[]) => {
  const groups: { date: string; messages: Message[] }[] = [];
  let currentDay = '';
  msgs.forEach((m) => {
    const day = new Date(m.createdAt).toDateString();
    if (day !== currentDay) {
      currentDay = day;
      groups.push({ date: m.createdAt, messages: [m] });
    } else {
      groups[groups.length - 1].messages.push(m);
    }
  });
  return groups;
};

const MessagesPage: React.FC = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialUserId = searchParams.get('userId');

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedUser, setSelectedUser] = useState<ChatUser | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const threadEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const fetchConversations = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const result = await apiService.getConversations();
      if (result.success) setConversations(result.conversations);
    } catch (err) {
      console.error('Error fetching conversations:', err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const fetchMessages = useCallback(async (userId: string, silent = false) => {
    try {
      if (!silent) setLoadingMessages(true);
      const result = await apiService.getMessages(userId);
      if (result.success) {
        setMessages(result.messages);
        if (result.user) setSelectedUser(result.user);
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      if (!silent) setLoadingMessages(false);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll for new messages every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (selectedUser) fetchMessages(selectedUser.id, true);
      fetchConversations(true);
    }, 10000);
    return () => clearInterval(interval);
  }, [selectedUser, fetchMessages, fetchConversations]);

  // Handle ?userId= deep link once conversations load
  useEffect(() => {
    if (initialUserId && conversations.length > 0 && !selectedUser) {
      const conv = conversations.find((c) => c.user.id === initialUserId);
      if (conv) {
        setMessages([]);
        setSelectedUser(conv.user);
        fetchMessages(conv.user.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialUserId, conversations]);

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const selectConversation = (u: ChatUser) => {
    setMessages([]);
    setSelectedUser(u);
    fetchMessages(u.id);
  };

  const handleSend = async () => {
    if (!inputText.trim() || !selectedUser || sending) return;
    const content = inputText.trim();
    setInputText('');
    setSending(true);

    const temp: Message = {
      id: `temp-${Date.now()}`,
      content,
      createdAt: new Date().toISOString(),
      isFromMe: true,
      sender: { id: 'me', name: 'Me', profilePicture: null },
    };
    setMessages((prev) => [...prev, temp]);

    try {
      const result = await apiService.sendMessage(selectedUser.id, content);
      if (result.success && result.message) {
        setMessages((prev) => prev.map((m) => (m.id === temp.id ? result.message! : m)));
        fetchConversations(true);
      } else {
        setMessages((prev) => prev.filter((m) => m.id !== temp.id));
        alert('Failed to send message. Please try again.');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setMessages((prev) => prev.filter((m) => m.id !== temp.id));
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const filtered = conversations.filter((c) =>
    c.user.name.toLowerCase().includes(search.toLowerCase())
  );
  const selectedConv = conversations.find((c) => c.user.id === selectedUser?.id);

  if (!user) return null;

  return (
    <div className="section" style={{ paddingTop: 40, paddingBottom: 48 }}>
      <div className="wrap">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            flexWrap: 'wrap',
            gap: 16,
            marginBottom: 24,
          }}
        >
          <div>
            <span className="eyebrow" style={{ display: 'block', marginBottom: 8 }}>
              {user.name}
            </span>
            <h1 style={{ fontSize: 30 }}>Messages</h1>
          </div>
          <p className="meta" style={{ maxWidth: 380 }}>
            Messages stay on-platform. Scheduling changes made here still need a confirmed
            session — use reschedule on the session itself.
          </p>
        </div>

        <div
          className="card"
          style={{
            padding: 0,
            display: 'grid',
            gridTemplateColumns: '300px 1fr',
            overflow: 'hidden',
            minHeight: 560,
          }}
        >
          {/* Left: conversation list */}
          <aside
            style={{
              borderRight: '1px solid var(--hairline)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--hairline)' }}>
              <input
                type="search"
                placeholder="Search conversations"
                style={{ fontSize: 14 }}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {loading ? (
              <p className="meta" style={{ padding: '14px 16px' }}>
                Loading conversations…
              </p>
            ) : filtered.length === 0 ? (
              <p className="meta" style={{ padding: '14px 16px' }}>
                {conversations.length === 0
                  ? 'No conversations yet.'
                  : 'No conversations match your search.'}
              </p>
            ) : (
              filtered.map((conv) => (
                <button
                  key={conv.user.id}
                  className={`convo${selectedUser?.id === conv.user.id ? ' on' : ''}`}
                  onClick={() => selectConversation(conv.user)}
                >
                  <Monogram name={conv.user.name} size={40} style={{ fontSize: 15 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <span className="who">{conv.user.name}</span>
                      {conv.lastMessage && (
                        <span className="meta">{formatConvoTime(conv.lastMessage.createdAt)}</span>
                      )}
                    </div>
                    <div className="preview">
                      {conv.lastMessage
                        ? `${conv.lastMessage.isFromMe ? 'You: ' : ''}${conv.lastMessage.content}`
                        : 'Start a conversation'}
                    </div>
                  </div>
                  {conv.unreadCount > 0 && <span className="unread-dot">{conv.unreadCount}</span>}
                </button>
              ))
            )}

            <div
              style={{
                padding: '14px 16px',
                marginTop: 'auto',
                borderTop: '1px solid var(--hairline)',
              }}
            >
              <p className="meta">
                Conversations open after a booked session.{' '}
                <Link to="/mentors" style={{ textDecoration: 'underline' }}>
                  Browse mentors →
                </Link>
              </p>
            </div>
          </aside>

          {/* Right: thread */}
          <section style={{ display: 'flex', flexDirection: 'column' }}>
            {selectedUser ? (
              <>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 12,
                    padding: '14px 20px',
                    borderBottom: '1px solid var(--hairline)',
                  }}
                >
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <Monogram name={selectedUser.name} size={36} style={{ fontSize: 14 }} />
                    <div>
                      <div style={{ fontWeight: 500 }}>{selectedUser.name}</div>
                      <div className="meta">
                        {roleLabel(selectedConv?.connectionType || '') || 'Conversation'}
                      </div>
                    </div>
                  </div>
                  {roleLabel(selectedConv?.connectionType || '') === 'Mentor' && (
                    <Link className="btn btn-secondary btn-sm" to="/mentors">
                      Book again
                    </Link>
                  )}
                </div>

                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                    padding: 20,
                    overflowY: 'auto',
                    maxHeight: 'calc(100vh - 380px)',
                  }}
                >
                  {loadingMessages ? (
                    <p className="meta">Loading messages…</p>
                  ) : messages.length === 0 ? (
                    <p className="meta">No messages yet — say hello to {selectedUser.name}.</p>
                  ) : (
                    groupByDay(messages).map((group, i) => (
                      <React.Fragment key={i}>
                        <div className="day-divider">{formatDayDivider(group.date)}</div>
                        {group.messages.map((m) => (
                          <div
                            key={m.id}
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: m.isFromMe ? 'flex-end' : 'flex-start',
                            }}
                          >
                            <div className={`msg ${m.isFromMe ? 'us' : 'them'}`}>{m.content}</div>
                            <div className="msg-meta">{formatTime(m.createdAt)}</div>
                          </div>
                        ))}
                      </React.Fragment>
                    ))
                  )}
                  <div ref={threadEndRef} />
                </div>

                <div
                  style={{
                    display: 'flex',
                    gap: 10,
                    padding: '14px 20px',
                    borderTop: '1px solid var(--hairline)',
                  }}
                >
                  <input
                    ref={inputRef}
                    placeholder="Write a message"
                    style={{ flex: 1 }}
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSend();
                    }}
                  />
                  <button
                    className="btn btn-primary"
                    onClick={handleSend}
                    disabled={!inputText.trim() || sending}
                  >
                    {sending ? '…' : 'Send'}
                  </button>
                </div>
              </>
            ) : (
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 40,
                  textAlign: 'center',
                }}
              >
                <div style={{ fontWeight: 500, marginBottom: 6 }}>Select a conversation</div>
                <p className="meta">
                  Pick someone on the left, or book a session to start a new one.
                </p>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
